// Shape of public/audio/manifest.json (written by scripts/audio/prepare.mjs). Pure helpers only.

export interface LoopEntry { name: string; rpm?: number }
export interface AudioManifest {
  formats: string[];                 // extensions present for every entry, e.g. ['ogg', 'm4a']
  engineLoops: LoopEntry[];          // on-throttle loops, ascending rpm
  offLoop?: LoopEntry;               // off-throttle / decel loop
  oneShots: { landing?: string[]; splash?: string[]; passBy?: string[] };
  ambience: { track?: string[]; enduro?: string[]; ring?: string[] };
}

/**
 * Folder of a bike's sample set (public/audio/bikes/2t or 4t, relative to the base URL). `query` is the ?engine=
 * override (2t | 4t) for listening tests; anything else is ignored.
 */
export function sampleSetPath(set: '2t' | '4t', query?: string | null): string {
  return `audio/bikes/${query === '2t' || query === '4t' ? query : set}/`;
}

export function parseManifest(raw: unknown): AudioManifest | null {
  if (!raw || typeof raw !== 'object') return null;
  const m = raw as Partial<AudioManifest>;
  if (!Array.isArray(m.formats) || m.formats.length === 0) return null;
  const loops = Array.isArray(m.engineLoops) ? m.engineLoops.filter((l) => l && typeof l.name === 'string' && typeof l.rpm === 'number') : [];
  loops.sort((a, b) => a.rpm! - b.rpm!);
  return {
    formats: m.formats.filter((f) => typeof f === 'string'),
    engineLoops: loops,
    offLoop: m.offLoop && typeof m.offLoop.name === 'string' ? m.offLoop : undefined,
    oneShots: m.oneShots ?? {},
    ambience: m.ambience ?? {},
  };
}

/** The short loop set (weak devices, the `audio` world module): idle, one middle and the top loop of an ascending list. */
export function shortLoops(loops: LoopEntry[]): LoopEntry[] {
  return loops.length <= 3 ? loops : [loops[0]!, loops[Math.floor(loops.length / 2)]!, loops[loops.length - 1]!];
}

/** Picks the first manifest format the browser can decode. canPlay mirrors HTMLMediaElement.canPlayType. */
export function pickFormat(formats: string[], canPlay: (mime: string) => string): string | null {
  const mime: Record<string, string> = { ogg: 'audio/ogg; codecs=opus', m4a: 'audio/mp4; codecs=mp4a.40.2', mp3: 'audio/mpeg', wav: 'audio/wav' };
  for (const f of formats) if (mime[f] && canPlay(mime[f]!) !== '') return f;
  return null;
}
